import React from 'react';

import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, Card, H1, Icon, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { AccountSettingsModal, CardLoader, SambaAutoLink, useFeatureFlag } from '@betterment/shared-components';
import { InlineErrorFallback } from '@betterment/shared-components/src/components/error-fallbacks';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { AccountPerformanceDetails } from './AccountPerformanceDetails';
import { CashAccountDetails } from './CashAccountDetails';
import { AccountOverviewEnvelopeAccountDocument } from './graphql/query.envelope.generated';

import {
  CLIENT_TYPE_NAME,
  ENVELOPE_ACCOUNT_DIRECT_TRADING_TYPE,
  ENVELOPE_ACCOUNT_MANAGED_TYPE,
} from '../../../Shared/constants';
import urlBuilder from '../../../Shared/url-builder';
import { ClientAccountSettingsModalContent } from '../../account-settings/ClientAccountSettingsModalContent';
import { HouseholdDocument } from '../HouseholdSummary/graphql/query.household.generated';

export const AccountCard: React.FC = () => {
  const { enabled: isDetailsEnabled } = useFeatureFlag({
    splitName: 'retail.b4a_ax_adi_account_details_q4_2025_enabled',
    context: 'AccountCard',
  });

  const householdId = useRequiredParam('householdId');
  const accountId = useRequiredParam('accountId');

  const { data: householdData } = useGraphQLQuery(HouseholdDocument, { variables: { id: householdId } });
  const { data: accountData } = useGraphQLQuery(AccountOverviewEnvelopeAccountDocument, {
    variables: { id: accountId },
  });

  if (!accountData || !householdData) return <CardLoader />;
  if (householdData.household.__typename === 'RecordNotFoundResult') return <InlineErrorFallback />;

  const hasPurpose = Boolean(accountData.envelopeAccount.envelope.purpose);
  const isSelfDirectedOrManagedAccount =
    accountData.envelopeAccount.__typename === ENVELOPE_ACCOUNT_MANAGED_TYPE ||
    accountData.envelopeAccount.__typename === ENVELOPE_ACCOUNT_DIRECT_TRADING_TYPE;
  const hasAllTimeGains = isDetailsEnabled && isSelfDirectedOrManagedAccount;

  const allTimeGains =
    accountData.envelopeAccount.__typename === ENVELOPE_ACCOUNT_MANAGED_TYPE ||
    accountData.envelopeAccount.__typename === ENVELOPE_ACCOUNT_DIRECT_TRADING_TYPE
      ? accountData.envelopeAccount.performance.totals.earned
      : null;

  const asOf =
    accountData.envelopeAccount.__typename === ENVELOPE_ACCOUNT_MANAGED_TYPE ||
    accountData.envelopeAccount.__typename === ENVELOPE_ACCOUNT_DIRECT_TRADING_TYPE
      ? accountData.envelopeAccount.performance.history.recencyDescription
      : null;

  const timeWeightedReturns =
    accountData.envelopeAccount.__typename === ENVELOPE_ACCOUNT_MANAGED_TYPE
      ? accountData.envelopeAccount.timeWeightedReturns
      : null;
  const clientId = accountData.envelopeAccount.accountHolders.find(
    accountHolder => accountHolder.__typename === CLIENT_TYPE_NAME
  )?.id;

  return (
    <Card
      css={{
        color: 'text',
        textStyle: 'text100',
        p: 'space.300',
      }}
    >
      <Card.Header>
        <SambaAutoLink
          css={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            gap: 'space.50',
            _hover: {
              textDecoration: 'underline',
            },
          }}
          variant="textSecondary"
          to={urlBuilder.getHouseholdSummaryPage.absolute({ householdId })}
        >
          <Icon name="CaretLeft" size="small" css={{ pt: '1px' }} />
          <Text variant="text100">{householdData.household.name}</Text>
        </SambaAutoLink>

        <Box
          css={{
            display: 'flex',
            mt: 'space.300',
            mb: 'space.300',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={accountData.envelopeAccount.icon.light}
              alt="Icon"
              className={css({ height: 'size.450', width: 'size.450', mr: 'space.150', alignSelf: 'center' })}
            />
            <Box>
              {hasPurpose && <Text variant="text100">{accountData.envelopeAccount.envelope.purpose?.name}</Text>}
              <H1 variant="title300">{accountData.envelopeAccount.name}</H1>
            </Box>
          </Box>
          {isDetailsEnabled && (
            <AccountSettingsModal
              trigger={
                <Button variant="ghostSecondary">
                  <Button.Icon name="Gear" />
                  Settings
                </Button>
              }
            >
              <ClientAccountSettingsModalContent
                accountId={accountId}
                currentPath={`/households/${householdId}/account/${accountId}`}
              />
            </AccountSettingsModal>
          )}
        </Box>
      </Card.Header>
      <Card.Content>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.100' }}>
          <Box css={{ display: 'flex', flexDirection: 'column' }}>
            <Text variant="text100">Balance</Text>
            <Text variant="text400">{formatMoney(accountData.envelopeAccount.balance)}</Text>
          </Box>
          {hasAllTimeGains && (
            <AccountPerformanceDetails
              accountTypeName={accountData.envelopeAccount.__typename}
              legacyId={
                accountData.envelopeAccount.__typename === ENVELOPE_ACCOUNT_MANAGED_TYPE
                  ? accountData.envelopeAccount.legacyId
                  : undefined
              }
              allTimeGains={allTimeGains}
              timeWeightedReturns={timeWeightedReturns}
              asOf={asOf}
              clientId={clientId}
              directTradingBalanceDisclosure={accountData.disclosures?.directTradingBalanceDisclosure}
              performanceDisclosure={accountData.disclosures?.performanceDisclosure}
            />
          )}
          {accountData.envelopeAccount.__typename === 'CashAccount' && (
            <CashAccountDetails accountData={accountData.envelopeAccount} />
          )}
        </Box>
      </Card.Content>
    </Card>
  );
};
